/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ImportPhonebookContactsModal.tsx
 🗓️ Created: 2025-01-06
 🧩 Description:
    Modal component for importing phonebook contacts from CSV file
────────────────────────────────────────────────────────────────*/

import { useState, useRef, useEffect } from 'react';
import { Upload, X, AlertCircle, CheckCircle, XCircle, Info } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { importContacts } from './api/phonebooksApi';
import type { CSVContact, ImportResult } from './types';

interface ImportPhonebookContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  phonebookId: number | null; // null means phonebook needs to be created first
  onSaveFirst?: () => Promise<number | null>; // Callback to save phonebook first, returns new ID
}

export default function ImportPhonebookContactsModal({
  isOpen,
  onClose,
  onSuccess,
  phonebookId,
  onSaveFirst,
}: ImportPhonebookContactsModalProps) {
  const { t } = useI18n();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [csvContent, setCsvContent] = useState<string | null>(null);
  const [rowCount, setRowCount] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset modal state when it opens
  useEffect(() => {
    if (isOpen) {
      setSelectedFile(null);
      setCsvContent(null);
      setRowCount(0);
      setResults(null);
      setError(null);
      setIsProcessing(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      setError(t('phonebooks.messages.invalidFileType', 'Invalid file type. Please upload a CSV file.'));
      return;
    }

    setSelectedFile(file);
    setError(null);
    setResults(null);

    // Read CSV file
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;

        // Validate CSV has content
        if (!content || content.trim().length === 0) {
          setError(t('phonebooks.messages.emptyFile', 'The CSV file is empty or invalid.'));
          setCsvContent(null);
          return;
        }

        // Count rows (excluding header)
        const lines = content.split('\n').filter((line) => line.trim());
        const count = lines.length > 1 ? lines.length - 1 : 0;

        setRowCount(count);
        setCsvContent(content);
      } catch (err) {
        setError(t('phonebooks.messages.importError', 'Failed to read CSV file.'));
        setCsvContent(null);
      }
    };
    reader.readAsText(file);
  };

  const parseCSV = (csvText: string): CSVContact[] => {
    const lines = csvText.split('\n').filter((line) => line.trim());
    if (lines.length < 2) {
      throw new Error(t('phonebooks.messages.emptyFile', 'The CSV file is empty or invalid.'));
    }

    const headers = lines[0].split(',').map((h) => h.trim());
    const contacts: CSVContact[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map((v) => v.trim());
      const contact: Record<string, string> = {};

      headers.forEach((header, index) => {
        contact[header] = values[index] || '';
      });

      // Validate required fields
      if (contact['first_name'] && contact['phone']) {
        contacts.push(contact as unknown as CSVContact);
      }
    }

    return contacts;
  };

  const handleImport = async () => {
    if (!csvContent) return;

    setIsProcessing(true);
    setError(null);

    try {
      let currentPhonebookId = phonebookId;

      // If phonebook doesn't exist, save it first
      if (!currentPhonebookId && onSaveFirst) {
        const newId = await onSaveFirst();
        if (!newId) {
          throw new Error(t('phonebooks.messages.saveError', 'Failed to save phonebook.'));
        }
        currentPhonebookId = newId;
      }

      if (!currentPhonebookId) {
        throw new Error(t('phonebooks.messages.saveError', 'Phonebook ID is required.'));
      }

      // Parse CSV
      const contacts = parseCSV(csvContent);

      if (contacts.length === 0) {
        throw new Error(t('phonebooks.messages.noValidContacts', 'No valid contacts found in CSV file.'));
      }

      // Import contacts
      const result = await importContacts(currentPhonebookId, contacts);
      setResults(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('phonebooks.messages.importError', 'Failed to import contacts.'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    // If there are results (imported), refresh the parent data
    if (results && results.imported > 0) {
      onSuccess();
    }

    setSelectedFile(null);
    setCsvContent(null);
    setRowCount(0);
    setResults(null);
    setError(null);
    onClose();
  };

  const handleBrowse = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Upload className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              {t('phonebooks.importTitle', 'Import Contacts from CSV')}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* File Selection */}
          {!results && (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  {t('phonebooks.import.upload', 'Upload CSV File')}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <button
                    onClick={handleBrowse}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-foreground rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    {t('phonebooks.import.browse', 'Browse')}
                  </button>
                  {selectedFile && (
                    <span className="text-sm text-muted-foreground">{selectedFile.name}</span>
                  )}
                </div>
              </div>

              {/* File Info */}
              {csvContent && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1 text-sm">
                      <p className="font-medium text-blue-900 dark:text-blue-100">
                        {t('phonebooks.import.fileInfo', 'File Information')}
                      </p>
                      <ul className="text-blue-700 dark:text-blue-300 space-y-1">
                        <li>• {t('phonebooks.import.totalContacts', 'Total Contacts')}: {rowCount}</li>
                        <li>• {t('phonebooks.import.fileFormat', 'File Format')}: CSV</li>
                        <li>• {t('phonebooks.import.requiredFields', 'Required Fields')}: first_name, phone</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Instructions */}
              <div className="p-4 bg-muted/50 border border-border rounded-lg">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">
                    {t('phonebooks.import.format', 'CSV Format')}:
                  </p>
                  <code className="text-xs text-muted-foreground block">
                    first_name,last_name,phone,mobile_number,home_number,organization,job_title,email
                  </code>
                  <code className="text-xs text-muted-foreground block mt-1">
                    John,Doe,+1234567890,+0987654321,,Acme Inc,Manager,john@example.com
                  </code>
                </div>
              </div>
            </>
          )}

          {/* Results */}
          {results && (
            <div className="space-y-4">
              {/* Success Banner */}
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-green-900 dark:text-green-100 mb-1">
                      {t('phonebooks.import.success', 'Import Completed')}
                    </p>
                    <p className="text-xs text-green-700 dark:text-green-300">
                      {results.imported} {t('phonebooks.import.contactsImported', 'contacts imported')}
                      {results.skipped > 0 && `, ${results.skipped} ${t('phonebooks.import.contactsSkipped', 'skipped')}`}
                      {results.errors.length > 0 && `, ${results.errors.length} ${t('phonebooks.import.errors', 'errors')}`}
                      {results.total && ` (${t('phonebooks.import.total', 'Total')}: ${results.total})`}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <span className="text-sm font-medium text-green-900 dark:text-green-100">
                      {t('phonebooks.import.imported', 'Imported')}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                    {results.imported}
                  </div>
                  <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                    {t('phonebooks.import.importedDesc', 'New contacts added to phonebook')}
                  </div>
                </div>

                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                    <span className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                      {t('phonebooks.import.skipped', 'Skipped')}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-300">
                    {results.skipped}
                  </div>
                  <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                    {t('phonebooks.import.skippedDesc', 'Duplicates or invalid contacts')}
                  </div>
                </div>

                {results.errors.length > 0 && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                      <span className="text-sm font-medium text-red-900 dark:text-red-100">
                        {t('phonebooks.import.errors', 'Errors')}
                      </span>
                    </div>
                    <div className="text-2xl font-bold text-red-700 dark:text-red-300">
                      {results.errors.length}
                    </div>
                    <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                      {t('phonebooks.import.errorsDesc', 'Contacts with validation errors')}
                    </div>
                  </div>
                )}

                <div className="p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Info className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {t('phonebooks.import.total', 'Total')}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                    {results.total ?? (results.imported + results.skipped + results.errors.length)}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {t('phonebooks.import.totalDesc', 'Total contacts processed')}
                  </div>
                </div>
              </div>

              {/* Errors List */}
              {results.errors && results.errors.length > 0 && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-2 flex-1">
                      <p className="text-sm font-medium text-red-900 dark:text-red-100">
                        {t('phonebooks.import.errors', 'Errors')} ({results.errors.length})
                      </p>
                      <ul className="text-xs text-red-700 dark:text-red-300 space-y-1 max-h-40 overflow-y-auto">
                        {results.errors.map((err, idx) => (
                          <li key={idx}>• {err}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-border">
          {!results ? (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-foreground hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                disabled={isProcessing}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleImport}
                disabled={!csvContent || isProcessing || (!phonebookId && !onSaveFirst)}
                className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t('phonebooks.actions.importing', 'Importing...')}
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    {t('phonebooks.actions.importFile', 'Import File')}
                  </>
                )}
              </button>
            </>
          ) : (
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              {t('common.close')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
